import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Loader2, Lock, Sparkles } from "lucide-react";
import { tierUnlocks, type ChatMessage, type PlanTier } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatMessage(text: string) {
  return escapeHtml(text)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

export function AriaChat({ userName }: { userName?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  // Listen for a global "open Aria" event so other parts of the app (e.g.
  // the Dashboard "Ask Aria" quick-action) can open the chat without
  // navigating to a non-existent /assistant route.
  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    window.addEventListener("aria:open", onOpen);
    return () => window.removeEventListener("aria:open", onOpen);
  }, []);
  const { user } = useAuth();
  const tier: PlanTier = ((user?.tier as PlanTier) || "free");
  const ariaUnlocked = tierUnlocks(tier, "aria");
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: messages = [] } = useQuery<ChatMessage[]>({
    queryKey: ["/api/chat/messages"],
    enabled: isOpen,
  });

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await apiRequest("POST", "/api/chat/send", { content });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat/messages"] });
    },
  });

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || sendMutation.isPending) return;
    setInput("");
    sendMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const firstName = userName?.split(" ")[0] || userName?.split("@")[0] || "there";

  const welcomeMessage = `Hi ${firstName}! 👋 Here's an onboarding video you can follow along with if you'd like. [Watch now →]\n\nI'm **Aria**, your DD AI assistant. I'll be helping you get your Google Business Profile fully optimized. Let's get started!`;

  const displayMessages = messages.length === 0
    ? [{ id: "welcome", role: "assistant", content: welcomeMessage, createdAt: new Date().toISOString() }]
    : messages;

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand-orange text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105"
          data-testid="button-aria-open"
        >
          {ariaUnlocked ? (
            <MessageCircle className="w-6 h-6" />
          ) : (
            <Lock className="w-5 h-5" />
          )}
        </button>
      )}

      {isOpen && !ariaUnlocked && (
        <div
          className="fixed bottom-6 right-3 left-3 sm:left-auto sm:right-6 z-50 sm:w-[360px] rounded-2xl border border-[#1E1E2E] bg-[#12121A] shadow-2xl overflow-hidden"
          data-testid="aria-chat-locked-panel"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1E1E2E]">
            <div className="w-8 h-8 rounded-full bg-brand-orange flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Aria</p>
              <p className="text-[10px] text-gray-400">DD AI Assistant</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#1E1E2E] transition-colors text-gray-400 hover:text-white"
              data-testid="button-aria-close-locked"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 space-y-4 text-center">
            <div className="w-12 h-12 rounded-full bg-brand-orange/20 flex items-center justify-center mx-auto">
              <Lock className="w-6 h-6 text-brand-orange" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Aria is a Growth feature</p>
              <p className="text-xs text-gray-400 mt-1">
                Upgrade to Growth or Dominator to unlock your AI co-pilot for GBP
                optimization, reviews, citations, and AI SEO.
              </p>
            </div>
            <Link href="/pricing">
              <Button
                className="w-full bg-brand-orange hover:bg-brand-orange/90"
                onClick={() => setIsOpen(false)}
                data-testid="button-aria-upgrade"
              >
                See plans
              </Button>
            </Link>
          </div>
        </div>
      )}

      {isOpen && ariaUnlocked && (
        <div className="fixed bottom-6 right-3 left-3 sm:left-auto sm:right-6 z-50 sm:w-[380px] max-h-[min(520px,calc(100vh-6rem))] flex flex-col rounded-2xl border border-[#1E1E2E] bg-[#12121A] shadow-2xl overflow-hidden" data-testid="aria-chat-panel">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1E1E2E]">
            <div className="w-8 h-8 rounded-full bg-brand-orange flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Aria</p>
              <p className="text-[10px] text-gray-400">DD AI Assistant</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#1E1E2E] transition-colors text-gray-400 hover:text-white"
              data-testid="button-aria-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[300px] max-h-[380px]">
            {displayMessages.map((msg: any) => (
              <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-full bg-brand-orange flex items-center justify-center shrink-0 mr-2 mt-1">
                    <span className="text-white font-bold text-[10px]">A</span>
                  </div>
                )}
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-brand-orange text-white rounded-br-sm"
                      : "bg-[#1E1E2E] text-gray-200 rounded-bl-sm"
                  }`}
                  dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                />
              </div>
            ))}
            {sendMutation.isPending && (
              <div className="flex justify-start">
                <div className="w-6 h-6 rounded-full bg-brand-orange flex items-center justify-center shrink-0 mr-2 mt-1">
                  <span className="text-white font-bold text-[10px]">A</span>
                </div>
                <div className="bg-[#1E1E2E] px-3 py-2 rounded-xl rounded-bl-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="px-4 py-3 border-t border-[#1E1E2E]">
            <div className="flex items-center gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Aria anything..."
                className="flex-1 bg-[#1E1E2E] border-none text-white placeholder:text-gray-500 text-sm h-9"
                data-testid="input-aria-message"
              />
              <Button
                size="icon"
                className="w-9 h-9 shrink-0 bg-brand-orange hover:bg-brand-orange/90"
                onClick={handleSend}
                disabled={!input.trim() || sendMutation.isPending}
                data-testid="button-aria-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-gray-500 text-center mt-2">
              Powered by Digital Domination
            </p>
          </div>
        </div>
      )}
    </>
  );
}
